/**
 * PanelDockGroup — the tabbed panel the Add palette and format toolbar merge
 * into when one is dragged onto the other (see panelDock). Illustrator-style
 * grouping, tabs variant (chosen design A): one floating panel, an
 * `Add | Format` tab strip, one body shown at a time, a "split" button that
 * pops the two back into separate panels, and a chevron that collapses the
 * group to just its tabs.
 *
 * Lives inside BOTH AddDragProvider (the Add tiles need useAddDrag) and
 * MultiEditorProvider (the format body needs the lexical hooks) — mounted next
 * to <AddSectionPalette/> in the editor screen, which is inside both. Desktop
 * only; the mobile panels never dock.
 *
 * Rendered UNCONDITIONALLY by the screen: it returns null until grouped, and
 * the inner GroupPanel is mounted fresh each time grouping starts (only rendered
 * while grouped) so useDraggablePanel seeds its position from the drop point.
 *
 * react-strict-dom (`html.*` + `css.create`). NOTE: RSD's `html.div` forces
 * `flex-grow: 0` on web, so the grip row centers the grip via absolute
 * positioning (not flex:1) and the collapse chevron pins right via
 * `margin-left: auto` (an auto margin, not flex-grow).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { html, css } from "react-strict-dom";
import { usePortalMount } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  spacing,
  microSpacing,
  radii,
} from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { keepEditorFocus } from "./FormatToolbar.js";
import { PANEL_REGISTRY } from "./panelRegistry.js";
import { useDraggablePanel } from "./useDraggablePanel.js";
import {
  panelDock,
  usePanelDock,
  TAB_LABEL,
  type DockTab,
} from "./panelDock.js";
import { groupIn } from "./panelAnim.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";

// Pointer travel (px) before a tab press becomes a tear-off rather than a
// tab switch — keeps a normal click from accidentally un-grouping.
const TEAR_THRESHOLD = 10;

const g = css.create({
  tab: {
    appearance: "none",
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: "transparent",
    borderBottomColor: "transparent",
    borderTopLeftRadius: radii.px6,
    borderTopRightRadius: radii.px6,
    marginBottom: -1,
    paddingTop: microSpacing.px5,
    paddingBottom: microSpacing.px5,
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    backgroundColor: "transparent",
    color: colors.textMuted,
    fontSize: "0.8rem",
    fontWeight: typography.fontWeightSemibold,
    cursor: "grab",
    touchAction: "none",
  },
  tabActive: {
    borderColor: colors.border,
    borderBottomColor: colors.bg,
    backgroundColor: colors.bg,
    color: colors.text,
  },
  tabTearing: { opacity: 0.4 },
  // ── tear ghost ──
  ghost: {
    position: "fixed",
    maxWidth: "min(96vw, 760px)",
    maxHeight: sizes.previewMaxH,
    zIndex: layers.popup,
    pointerEvents: "none",
    opacity: 0.92,
    transform: "scale(1.02)",
    transformOrigin: "40px 12px", // under the grabbed tab
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.bg,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.infoBorder,
    borderRadius: radii.sm,
    boxShadow: shadows.lg,
    overflow: "hidden",
  },
  ghostPos: (left: number, top: number) => ({ left, top }),
  ghostWidthAdd: { width: sizes.sidebarW },
  ghostWidthFmt: { width: "max-content" },
  ghostGrip: {
    height: sizes.handle,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.border,
    fontSize: "0.7rem",
    letterSpacing: "0.15em",
    backgroundColor: "transparent",
  },
  ghostGripAdd: { backgroundColor: colors.bgCard },
  ghostBody: { overflow: "hidden" },
  // ── group panel ──
  panel: {
    position: "fixed",
    width: sizes.sidebarW,
    zIndex: layers.modal,
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.bg,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.sm,
    boxShadow: shadows.md,
    overflow: "hidden",
  },
  panelPos: (left: number, top: number, maxHeight: string) => ({
    left,
    top,
    maxHeight,
  }),
  // Grip row: relative so the grip fills it via absolute inset (RSD kills
  // flex-grow), with the split button pinned to the right on top.
  gripRow: {
    position: "relative",
    height: sizes.chip,
    backgroundColor: colors.bgCard,
  },
  grip: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "move",
    color: colors.border,
    touchAction: "none",
    userSelect: "none",
    fontSize: "0.7rem",
    letterSpacing: "0.15em",
  },
  splitBtn: {
    position: "absolute",
    top: 0,
    right: 0,
    height: "100%",
    width: sizes.chip,
    zIndex: layers.raised,
    borderStyle: "none",
    backgroundColor: "transparent",
    color: colors.textMuted,
    cursor: "pointer",
    fontSize: "0.8rem",
    lineHeight: 1,
  },
  tabStrip: {
    display: "flex",
    alignItems: "flex-end",
    gap: microSpacing.px2,
    paddingTop: spacing.xs,
    paddingLeft: spacing.xs,
    paddingRight: spacing.xs,
    backgroundColor: colors.bgCard,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  collapseBtn: {
    marginLeft: "auto", // pin right without flex-grow
    flexShrink: 0,
    width: sizes.chip,
    height: sizes.chip,
    marginBottom: microSpacing.px3,
    borderStyle: "none",
    backgroundColor: "transparent",
    color: colors.textMuted,
    cursor: "pointer",
    fontSize: "0.7rem",
    lineHeight: 1,
  },
  // A dashed placeholder tab shown in the strip while another panel is being
  // dragged ONTO the group (marks where the incoming tab will insert).
  ghostTab: {
    borderWidth: borders.hairline,
    borderStyle: "dashed",
    borderColor: colors.accent,
    borderTopLeftRadius: radii.px6,
    borderTopRightRadius: radii.px6,
    marginBottom: -1,
    paddingTop: microSpacing.px5,
    paddingBottom: microSpacing.px5,
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    backgroundColor: colors.infoBg,
    color: colors.accent,
    fontSize: "0.8rem",
    fontWeight: typography.fontWeightSemibold,
    whiteSpace: "nowrap",
  },
  // Accent ring while the group is the active drop target.
  panelDocking: {
    boxShadow: shadows.dropOver,
  },
});

/**
 * A group tab. A plain click switches the active tab; dragging it out past
 * TEAR_THRESHOLD tears its panel off the group (the inverse of drag-to-group)
 * and drops it under the pointer — a live ghost follows during the drag. Uses
 * pointerdown + window listeners (not onClick) so the same gesture decides
 * switch-vs-tear on release.
 */
function TabButton({
  tab,
  label,
  active,
  tearing,
  onActivate,
}: {
  tab: DockTab;
  label: string;
  active: boolean;
  tearing: boolean;
  onActivate: (tab: DockTab) => void;
}) {
  const onPointerDown = (e: React.PointerEvent) => {
    const start = { x: e.clientX, y: e.clientY };
    let torn = false;
    const onMove = (ev: PointerEvent) => {
      if (
        !torn &&
        Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > TEAR_THRESHOLD
      ) {
        torn = true;
        panelDock.setTearing(tab);
      }
      if (torn) panelDock.setTearPoint(ev.clientX, ev.clientY);
    };
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      if (torn) panelDock.tearOff(tab, ev.clientX, ev.clientY);
      else onActivate(tab);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };
  return (
    <html.button
      type="button"
      onPointerDown={onPointerDown}
      aria-pressed={active}
      style={[g.tab, active && g.tabActive, tearing && g.tabTearing]}
    >
      {label}
    </html.button>
  );
}

/** The drag ghost shown while a tab is being torn off — a preview of the LONE
 *  panel it becomes (grip + that panel's real body, no tab strip: it's leaving
 *  the group, so the other tab isn't part of it), lifted and slightly
 *  transparent, following the pointer. Portalled into the ThemeProvider mount
 *  at layers.popup — like Duro's DragDrop ghost — so it sits above the group
 *  container (which clips) and above an open Dialog, as a fixed, inert
 *  (pointer-transparent) overlay; it renders nothing until the mount exists.
 *  Sized like the standalone panel it turns into: the Add palette is
 *  sizes.sidebarW, the format panel is content-width — so the drop lands at
 *  the same size (no jump). */
function TearGhost({
  tab,
  point,
}: {
  tab: DockTab;
  point: { x: number; y: number };
}) {
  const def = PANEL_REGISTRY[tab];
  const Body = def.Body;
  const mount = usePortalMount();
  const ghostRef = useRef<HTMLDivElement>(null);
  // The portal leaves the editor's canvas scope ([data-wb-scope]), which pins
  // --wb-vw, the authoring width fluid type scales against. Copy it onto the
  // ghost so anything in its body that sizes from it renders as docked. The
  // brand stays on page bodies, never on chrome, so nothing else is copied.
  // The value is captured once per drag (the ghost mounts when the tear
  // starts): a breakpoint change mid-drag is not followed.
  // querySelector takes the first [data-wb-scope]; today only EditorScreen
  // renders one.
  useLayoutEffect(() => {
    const el = ghostRef.current;
    const scope = document.querySelector<HTMLElement>("[data-wb-scope]");
    if (!el || !scope) return;
    const vw = scope.style.getPropertyValue("--wb-vw");
    if (vw) el.style.setProperty("--wb-vw", vw);
  }, [mount]);
  if (!mount) return null;
  return createPortal(
    <html.div
      ref={ghostRef}
      aria-hidden
      style={[
        g.ghost,
        g.ghostPos(point.x - 40, point.y - 12),
        def.ghostWidth === "max-content" ? g.ghostWidthFmt : g.ghostWidthAdd,
      ]}
    >
      {/* Grip only — matches the lone panel's chrome (no tabs). */}
      <html.div style={[g.ghostGrip, def.ghostGripTinted && g.ghostGripAdd]}>
        ⠿⠿⠿
      </html.div>
      <html.div style={g.ghostBody}>
        <Body />
      </html.div>
    </html.div>,
    mount,
  );
}

function GroupPanel({
  activeTab,
  initialPos,
  fromWidth,
}: {
  activeTab: DockTab;
  initialPos: { x: number; y: number };
  fromWidth: number;
}) {
  const { tearing, tearPoint, members, candidate, candidateSide, dragSource } =
    usePanelDock();
  const [collapsed, setCollapsed] = useState(false);
  // Activating a tab always expands (clicking a collapsed tab opens it to that
  // tab); the chevron is the explicit collapse toggle.
  const activate = (tab: DockTab) => {
    panelDock.setActiveTab(tab);
    setCollapsed(false);
  };
  const { ref, pos, onHandleDown } = useDraggablePanel(initialPos, {
    onDrop: (pt) => panelDock.setGroupPos({ x: pt.x, y: pt.y }),
  });
  // Report the group's live top-left so an ungroup can glide panels from here.
  useEffect(() => {
    panelDock.setGroupOrigin(pos.x, pos.y);
  }, [pos.x, pos.y]);
  // Register the group's rect so a standalone panel dragged onto it can dock
  // (append a tab) — mirrors each panel's own registerRect.
  useEffect(
    () =>
      panelDock.registerRect(
        "group",
        () => ref.current?.getBoundingClientRect() ?? null,
      ),
    [ref],
  );
  // Settle-in + grow from the panel it docked over (mounts fresh each grouping).
  useLayoutEffect(() => {
    if (ref.current) groupIn(ref.current, fromWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A panel is being dragged onto the group → preview the incoming tab.
  const docking = candidate === "group" && dragSource != null;
  const ActiveBody = PANEL_REGISTRY[activeTab].Body;

  return (
    <>
      <html.div
        ref={ref}
        data-testid="wb-dock-group"
        // Keep the text editor focused while interacting with the group chrome,
        // so switching to the Add tab (or grabbing the grip) doesn't grey the
        // format controls — same guard the standalone format panel uses.
        onMouseDown={keepEditorFocus}
        style={[
          g.panel,
          g.panelPos(pos.x, pos.y, `calc(100vh - ${pos.y + 24}px)`),
          docking && g.panelDocking,
        ]}
      >
        {/* Grip (fills the row) + split (pop-out) button pinned right. */}
        <html.div style={g.gripRow}>
          <html.div onPointerDown={onHandleDown} style={g.grip}>
            ⠿⠿⠿
          </html.div>
          <html.button
            type="button"
            onClick={() => panelDock.ungroup()}
            aria-label="Split into separate panels"
            style={g.splitBtn}
          >
            ⇲
          </html.button>
        </html.div>

        {/* Tab strip + collapse chevron (collapses the group to just its tabs;
            clicking a tab while collapsed expands it back to that tab). A dashed
            ghost tab previews an incoming panel at the hovered side. */}
        <html.div style={g.tabStrip}>
          {docking && candidateSide === "left" && (
            <html.span aria-hidden style={g.ghostTab}>
              {TAB_LABEL[dragSource]}
            </html.span>
          )}
          {members.map((id) => (
            <TabButton
              key={id}
              tab={id}
              label={TAB_LABEL[id]}
              active={id === activeTab}
              tearing={tearing === id}
              onActivate={activate}
            />
          ))}
          {docking && candidateSide !== "left" && (
            <html.span aria-hidden style={g.ghostTab}>
              {TAB_LABEL[dragSource]}
            </html.span>
          )}
          <html.button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand panel" : "Collapse panel"}
            aria-expanded={!collapsed}
            style={g.collapseBtn}
          >
            {collapsed ? "▸" : "▾"}
          </html.button>
        </html.div>

        {/* Active body (hidden while collapsed). */}
        {!collapsed && <ActiveBody />}
      </html.div>
      {tearing && <TearGhost tab={tearing} point={tearPoint} />}
    </>
  );
}

export function PanelDockGroup() {
  const { grouped, activeTab, groupPos, groupFromWidth } = usePanelDock();
  if (!grouped) return null;
  return (
    <GroupPanel
      activeTab={activeTab}
      initialPos={groupPos}
      fromWidth={groupFromWidth}
    />
  );
}
